import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import DriverPoolSection from "@/components/DriverPoolSection";
import { DriverCardHeader, DriverCardActions, DriverItemsList, DriverCloseSection } from "@/components/DriverCardParts";
import { MultiPhotoUploader } from "@/components/MultiPhotoUploader";

/**
 * Mobile-first ticket card used in the Driver Day list.
 * Fully self-contained — owns its pools/gallons/photos state, calls the API directly
 * to update + close the ticket, then asks the parent to refresh via onClose.
 */
export default function DriverTicketCard({ t, settings, onClose, distanceMiles, routeIndex, nextStopInfo, showPrices }) {
  const [open, setOpen] = useState(false);
  const [pools, setPools] = useState(() =>
    (t.pool_deliveries || []).map((d) => ({ ...d, gallons: Number(d.gallons || 0) }))
  );
  const [gallons, setGallons] = useState(Number(t.gallons || 0));
  // Carboys (5-gallon jug refill) — driver enters the exact count they swapped
  // on-site. Always defaults to 0 so the driver consciously types in the number
  // and we never accidentally bill the customer for a refill that didn't happen.
  const [carboysQty, setCarboysQty] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [entryEditIdx, setEntryEditIdx] = useState(null);
  const [entryDraft, setEntryDraft] = useState({ entry_photo_data_url: "", entry_instructions: "" });
  // Optional product-delivery proof photos. Driver Day, products-only tickets:
  // driver can attach 0..many images (signed receipt, "left at gate", etc.).
  // NOT AI-validated — these are just for the customer's invoice record.
  const [deliveryPhotos, setDeliveryPhotos] = useState(() => t.delivery_photo_data_urls || []);

  const startEntryEdit = (idx, d) => {
    setEntryEditIdx(idx);
    setEntryDraft({ entry_photo_data_url: d.entry_photo_data_url || "", entry_instructions: d.entry_instructions || "" });
  };
  const saveEntryEdit = async (idx) => {
    const d = pools[idx];
    if (!d?.pool_id) return toast.error("Missing pool reference");
    try {
      await api.patch(`/pools/${d.pool_id}/entry`, {
        entry_photo_data_url: entryDraft.entry_photo_data_url || "",
        entry_instructions: entryDraft.entry_instructions || "",
      });
      setPools(pools.map((x, i) => i === idx ? { ...x, entry_photo_data_url: entryDraft.entry_photo_data_url || "", entry_instructions: entryDraft.entry_instructions || "" } : x));
      setEntryEditIdx(null);
      toast.success("Entry info updated");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const totalGallons = pools.reduce((s, d) => s + Number(d.gallons || 0), 0) +
    (pools.length === 0 ? gallons : 0);
  const items = t.items || [];
  const isProductsOnly = t.products_only ||
    (pools.length === 0 && Number(t.gallons || 0) <= 0 && items.length > 0);
  const itemsTotal = items.reduce((s, i) => s + Number(i.price || 0) * Number(i.quantity || 0), 0);
  const subtotal = totalGallons * Number(settings.price_per_gallon || 5) + itemsTotal;

  // Coords of THIS stop (just-closed location); used to compute ETA to next stop
  const thisCoord = (() => {
    for (const d of pools) {
      if (d.latitude != null && d.longitude != null) return { lat: Number(d.latitude), lng: Number(d.longitude) };
    }
    if (t.customer_latitude != null && t.customer_longitude != null) {
      return { lat: Number(t.customer_latitude), lng: Number(t.customer_longitude) };
    }
    return null;
  })();

  const submitClose = async () => {
    const hasBillable = totalGallons > 0 || items.length > 0 || carboysQty > 0;
    if (!hasBillable) return toast.error("Enter gallons, carboys, or at least one product first");
    if (!confirmed && !isProductsOnly) return toast.error("Confirm the gallons delivered");
    const missingPhotos = pools.filter((p) => !((p.photo_before_data_url || "").trim() && (p.photo_after_data_url || "").trim()));
    if (missingPhotos.length > 0) {
      const names = missingPhotos.map((p) => p.pool_name || "(unnamed pool)").join(", ");
      return toast.error(`Take BOTH before and after photos for every pool before closing. Missing on: ${names}`);
    }
    setBusy(true);
    try {
      await api.put(`/tickets/${t.id}`, {
        delivery_date: t.delivery_date,
        notes: t.notes || "",
        pool_deliveries: pools.map(({ pool_id, gallons: g, photo_before_data_url, photo_after_data_url }) => ({
          pool_id,
          gallons: Number(g || 0),
          photo_before_data_url: photo_before_data_url || "",
          photo_after_data_url: photo_after_data_url || "",
        })),
        gallons: Number(gallons || 0),
        items: (t.items || []).filter((it) => !((it.name || "").toLowerCase().includes("carboy")))
                              .map(({ product_id, quantity }) => ({ product_id, quantity })),
        refill_carboys: t.refill_empty_jugs && carboysQty > 0,
        refill_carboys_qty: t.refill_empty_jugs ? carboysQty : 0,
        // Optional proof-of-delivery photos — used by products-only tickets.
        delivery_photo_data_urls: deliveryPhotos,
      });
      const r = await api.post(`/tickets/${t.id}/close`);
      toast.success(`Invoice ${r.data.number} created`);
      if (nextStopInfo?.nextTicketId) {
        try {
          const nresp = await api.post(`/tickets/${t.id}/notify-next`, {
            next_ticket_id: nextStopInfo.nextTicketId,
            prev_lat: thisCoord?.lat,
            prev_lng: thisCoord?.lng,
          });
          if (nresp.data?.ok) {
            toast.success(`Next stop ETA sent · ~${nresp.data.eta_min ?? "?"} min`);
          } else if (nresp.data?.result?.skipped_reason === "no_or_invalid_phone") {
            toast.message(`Next stop has no phone — SMS skipped`);
          }
        } catch (e) { logError("Next-stop SMS failed (non-blocking):", e); }
      }
      onClose && onClose();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const firstAddr = pools[0]?.pool_address || t.customer_address;
  const missingPhotoPools = pools.filter((p) => !((p.photo_before_data_url || "").trim() && (p.photo_after_data_url || "").trim()));
  const photosOk = missingPhotoPools.length === 0;

  return (
    <article className="border border-border bg-background rounded-sm shadow-sm" data-testid={`driver-card-${t.id}`}>
      <DriverCardHeader
        t={t} open={open} onToggle={() => setOpen(!open)}
        routeIndex={routeIndex} distanceMiles={distanceMiles} firstAddr={firstAddr}
        isProductsOnly={isProductsOnly} items={items} pools={pools}
        totalGallons={totalGallons} subtotal={subtotal} showPrices={showPrices}
      />

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border">
          <DriverCardActions t={t} pools={pools} firstAddr={firstAddr} />
          {pools.length === 0 && !isProductsOnly && (
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gallons delivered</label>
              <Input type="number" inputMode="decimal" step="0.1" min="0" className="rounded-sm h-12 text-lg mono-num"
                     value={gallons} onChange={(e) => setGallons(parseFloat(e.target.value || 0))}
                     data-testid={`driver-fallback-gallons-${t.id}`} />
            </div>
          )}
          {t.refill_empty_jugs && !isProductsOnly && (
            <div className="border border-primary bg-primary/5 rounded-sm px-4 py-3 flex items-center gap-3" data-testid={`driver-carboys-block-${t.id}`}>
              <div className="flex-1">
                <div className="font-bold text-sm text-primary">Carboys refilled (5-gallon jugs)</div>
                <div className="text-xs text-muted-foreground">Enter how many you actually swapped on-site — flows straight to the invoice.</div>
              </div>
              <Input
                type="number" inputMode="numeric" min="0" step="1"
                className="rounded-sm h-12 w-20 text-lg mono-num text-center"
                value={carboysQty}
                onChange={(e) => setCarboysQty(Math.max(0, parseInt(e.target.value || 0)))}
                data-testid={`driver-carboys-qty-${t.id}`}
              />
            </div>
          )}
          {(() => {
            // Hide carboy lines from the static "Products to deliver" list when the
            // editable block above is the source of truth — prevents drivers seeing
            // two different carboy quantities at once.
            const visibleItems = t.refill_empty_jugs
              ? items.filter((it) => !((it.name || "").toLowerCase().includes("carboy")))
              : items;
            return visibleItems.length > 0 && <DriverItemsList ticketId={t.id} items={visibleItems} />;
          })()}
          {pools.map((d, idx) => (
            <DriverPoolSection
              key={d.pool_id}
              t={t} d={d} idx={idx} pools={pools} setPools={setPools}
              entryEdit={{
                entryEditIdx, entryDraft, setEntryDraft, startEntryEdit, saveEntryEdit,
                cancelEntryEdit: () => setEntryEditIdx(null),
              }}
            />
          ))}

          {/* Optional proof-of-delivery photos for products-only tickets.
              Multiple, optional, NOT AI-scanned — purely a paper trail. */}
          {isProductsOnly && (
            <div className="border border-border bg-secondary/20 rounded-sm p-3" data-testid={`driver-delivery-photos-${t.id}`}>
              <MultiPhotoUploader
                value={deliveryPhotos}
                onChange={setDeliveryPhotos}
                label="Delivery photos (optional)"
                testId={`driver-delivery-photos-${t.id}-uploader`}
                capture="environment"
              />
              <p className="text-[11px] text-muted-foreground mt-2">
                Optional — attach any photos that prove the delivery (signed receipt, package on porch, gate combo confirmation, etc.). Not required to close the ticket.
              </p>
            </div>
          )}

          {t.notes && (
            <div className="text-xs border-l-2 border-primary/60 pl-3 py-1 bg-secondary/40">
              <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Notes</div>
              {t.notes}
            </div>
          )}

          <DriverCloseSection
            t={t} isProductsOnly={isProductsOnly} confirmed={confirmed} setConfirmed={setConfirmed}
            missingPhotoPools={missingPhotoPools} photosOk={photosOk}
            busy={busy} showPrices={showPrices} subtotal={subtotal} onSubmit={submitClose}
          />
        </div>
      )}
    </article>
  );
}
